import React from 'react';
import { Box, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import {
  cardSx,
  cardTitleSx,
  formatCompact,
  formatFull,
  markSpecs,
  seriesPrimary,
  vizInk,
} from '../../../styles/analyticsStyles';

// Hand-rolled SVG and CSS bars rather than a charting library: this project has no chart dependency,
// and a printable sheet wants vector output whose text stays selectable and crisp at print
// resolution — which is also why the PDF path is the browser's own print engine rather than a canvas
// rasteriser.
//
// Every chart on this sheet is a single series, so none of them carries a legend: the panel title
// names the one thing being measured and the row label carries identity. The categorical trio in
// analyticsStyles is deliberately untouched here — introducing a second hue to a one-series bar
// would encode nothing and would put the validated palette back in play for no gain.

/** How a figure's provenance prints. Kept beside the number, never in a footnote nobody reads. */
export const sourceLabels = {
  EXCHANGE_DECLARED: 'Declared by exchanges',
  PARTNERSHIP_TARGETS: 'From partnership commitments',
  NOT_CAPTURED: 'Not captured yet',
};

/**
 * A headline figure. Per the visualization guidance a single number is not a chart — a one-bar bar
 * chart or a two-slice pie would be the wrong form here, so these are plain stat tiles.
 *
 * <p>When {@code source} is NOT_CAPTURED the tile prints an em dash rather than 0. A zero that means
 * "nobody has filled this in" must not look like a measured zero on a page someone is about to
 * quote in a pitch.
 */
export function StatTile({ label, value, unit, hint, source }) {
  const uncaptured = source === 'NOT_CAPTURED';

  return (
    <Box sx={{ ...cardSx, flex: '1 1 0', minWidth: 0 }}>
      <Typography sx={{ ...cardTitleSx, marginBottom: '6px' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography
          sx={{
            fontSize: 34,
            lineHeight: '40px',
            fontWeight: 700,
            color: uncaptured ? vizInk.muted : vizInk.primary,
            // Proportional figures on purpose — see formatCompact's note in analyticsStyles.
            fontVariantNumeric: 'normal',
          }}
          title={uncaptured ? 'Not captured yet' : formatFull(value)}
        >
          {uncaptured ? '—' : formatCompact(value)}
        </Typography>
        {unit && !uncaptured ? (
          <Typography sx={{ fontSize: 13, lineHeight: '18px', color: vizInk.secondary }}>{unit}</Typography>
        ) : null}
      </Box>
      {hint ? (
        <Typography sx={{ fontSize: 12, lineHeight: '16px', color: vizInk.secondary, marginTop: '2px' }}>
          {hint}
        </Typography>
      ) : null}
      {source ? (
        <Typography sx={{ fontSize: 10, lineHeight: '14px', color: vizInk.muted, marginTop: '4px' }}>
          {sourceLabels[source] || source}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Covered-of-total for one tier of the hierarchy, with a proportion meter under it.
 *
 * The meter is the point of the tile: "148 blocks" is a number, "148 of 7,013" is the market-
 * expansion story, and the bar makes the ratio legible without the reader dividing in their head.
 * It is a meter rather than a chart — one part-to-whole value, so no axis and no legend.
 */
export function CoverageTile({ label, covered, total }) {
  const share = total > 0 ? (covered / total) * 100 : 0;

  return (
    <Box sx={{ ...cardSx, flex: '1 1 0', minWidth: 0 }}>
      <Typography sx={{ ...cardTitleSx, marginBottom: '6px' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography
          sx={{ fontSize: 34, lineHeight: '40px', fontWeight: 700, color: vizInk.primary }}
          title={formatFull(covered)}
        >
          {formatCompact(covered)}
        </Typography>
        <Typography sx={{ fontSize: 13, lineHeight: '18px', color: vizInk.secondary }}>
          of {formatFull(total)}
        </Typography>
      </Box>
      <Box
        role="img"
        aria-label={`${covered} of ${total} covered, ${share.toFixed(1)} percent`}
        sx={{
          position: 'relative',
          height: 8,
          marginTop: '10px',
          backgroundColor: vizInk.plane,
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            // A covered tier is never drawn as nothing: at national scale the honest share can be a
            // fraction of a percent, and a bar of zero width reads as "no presence" rather than "few".
            width: `${covered > 0 ? Math.max(1.5, share) : 0}%`,
            backgroundColor: seriesPrimary,
            borderRadius: `0 ${markSpecs.barEndRadius}px ${markSpecs.barEndRadius}px 0`,
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 11, lineHeight: '16px', color: vizInk.muted, marginTop: '4px' }}>
        {share.toFixed(share < 10 ? 1 : 0)}% covered
      </Typography>
    </Box>
  );
}

/**
 * The breakdown one tier below whatever the sheet is currently showing — states under a country,
 * districts under a state, blocks under a district — and the way down into the next one.
 *
 * One panel does the work of both a chart and a table: the bar gives the ranking at a glance, the
 * numeric columns beside it make every value quotable. Splitting them into a chart panel and a
 * table panel would print the same data twice and cost a page.
 *
 * Rows arrive pre-sorted by the server on the metric the bar encodes, so the longest bar is always
 * first and the eye never hunts for the leader. Below the state tier that metric stops being
 * exchanges: a district with no exchange yet still has farmers, and a panel of empty bars measures
 * nothing. So when no row has an exchange the bar switches to farmers onboarded and the panel says
 * so in its title — the sort order already agrees, because farmers are the server's tie-break.
 */
export function LocationBreakdown({ rows: rowData, rowLabel, coverageLabel, areaUnit, onOpen }) {
  const rows = rowData || [];
  const tier = rowLabel || 'area';
  if (!rows.length) {
    return (
      <EmptyPanel
        title={`Breakdown by ${tier}`}
        message={`No ${tier.toLowerCase()} here has a user, a farmer or an exchange on it yet.`}
      />
    );
  }

  // Only rows with something under them are worth opening; the rest would land on an empty sheet.
  const drillable = typeof onOpen === 'function';
  const byExchanges = rows.some((row) => (row.exchanges || 0) > 0);
  const metric = byExchanges ? 'exchanges' : 'farmersOnboarded';
  const metricLabel = byExchanges ? 'Exchanges established' : 'Farmers onboarded';
  const max = rows.reduce((acc, row) => Math.max(acc, row[metric] || 0), 0) || 1;
  const totals = rows.reduce(
    (acc, row) => ({
      exchanges: acc.exchanges + (row.exchanges || 0),
      farmers: acc.farmers + (row.farmersOnboarded || 0),
      covered: acc.covered + (row.coveredDeeper || 0),
      potentialFarmers: acc.potentialFarmers + (row.potentialFarmers || 0),
      potentialAcres: acc.potentialAcres + Number(row.potentialAcres || 0),
    }),
    { exchanges: 0, farmers: 0, covered: 0, potentialFarmers: 0, potentialAcres: 0 }
  );

  const gridSx = coverageLabel ? rowGridSx : rowGridNoCoverageSx;

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={cardTitleSx}>
          {metricLabel} — by {tier}
        </Typography>
        <Typography sx={{ ...cardTitleSx, color: vizInk.secondary }}>
          {formatFull(byExchanges ? totals.exchanges : totals.farmers)} across {rows.length} {tier.toLowerCase()}
          {rows.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={gridSx}>
          <Typography sx={columnHeadSx}>{tier}</Typography>
          <Typography sx={columnHeadSx}>&nbsp;</Typography>
          <Typography sx={{ ...columnHeadSx, textAlign: 'right' }}>Exchanges</Typography>
          <Typography sx={{ ...columnHeadSx, textAlign: 'right' }}>Farmers</Typography>
          {coverageLabel ? (
            <Typography sx={{ ...columnHeadSx, textAlign: 'right' }}>{coverageLabel}</Typography>
          ) : null}
          <Typography sx={{ ...columnHeadSx, textAlign: 'right' }}>Farmer reach</Typography>
          <Typography sx={{ ...columnHeadSx, textAlign: 'right' }}>{areaUnit}</Typography>
        </Box>

        {rows.map((row) => {
          const opens = drillable && row.hasChildren;
          return (
            <Box
              key={row.locationId}
              sx={{
                ...gridSx,
                ...(opens ? openableRowSx : null),
              }}
              // A row is a link into the next level, so it carries the semantics of one: focusable,
              // announced as a button, and openable from the keyboard. Rows with nothing beneath
              // them stay inert rather than offering a dead end.
              {...(opens
                ? {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => onOpen(row),
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onOpen(row);
                      }
                    },
                    'aria-label': `Open ${row.locationName}`,
                  }
                : null)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    lineHeight: '18px',
                    fontWeight: 500,
                    color: vizInk.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.locationName}
                >
                  {row.locationName}
                </Typography>
                {opens ? (
                  // The affordance has to be visible without hovering — on a static screenshot or a
                  // printed page nobody discovers a hover-only cue — but it is chrome, not data, so
                  // it is muted and it does not print.
                  <Box
                    className="analytics-no-print"
                    component="span"
                    sx={{ display: 'flex', color: vizInk.muted, flexShrink: 0 }}
                  >
                    <Icon icon="material-symbols:chevron-right-rounded" width="16" height="16" />
                  </Box>
                ) : null}
              </Box>

              <Box sx={{ height: 12, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Box
                  title={`${row.locationName}: ${formatFull(row[metric])} ${metricLabel.toLowerCase()}`}
                  sx={{
                    height: 12,
                    width: `${Math.max(1, ((row[metric] || 0) / max) * 100)}%`,
                    backgroundColor: seriesPrimary,
                    // Square at the baseline, rounded at the data end.
                    borderRadius: `0 ${markSpecs.barEndRadius}px ${markSpecs.barEndRadius}px 0`,
                    minWidth: 2,
                  }}
                />
              </Box>

              <Typography sx={{ ...numberCellSx, fontWeight: 700, color: vizInk.primary }}>
                {formatFull(row.exchanges)}
              </Typography>
              <Typography sx={numberCellSx}>{formatFull(row.farmersOnboarded)}</Typography>
              {coverageLabel ? (
                <Typography sx={numberCellSx}>{formatFull(row.coveredDeeper)}</Typography>
              ) : null}
              <Typography sx={numberCellSx}>{dashIfZero(row.potentialFarmers)}</Typography>
              <Typography sx={numberCellSx}>{dashIfZero(row.potentialAcres)}</Typography>
            </Box>
          );
        })}

        <Box sx={{ ...gridSx, borderTop: `1px solid ${vizInk.gridline}`, paddingTop: '10px' }}>
          <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 700, color: vizInk.secondary }}>
            Total
          </Typography>
          <Typography sx={columnHeadSx}>&nbsp;</Typography>
          <Typography sx={{ ...numberCellSx, fontWeight: 700, color: vizInk.primary }}>
            {formatFull(totals.exchanges)}
          </Typography>
          <Typography sx={{ ...numberCellSx, fontWeight: 700 }}>{formatFull(totals.farmers)}</Typography>
          {coverageLabel ? (
            <Typography sx={{ ...numberCellSx, fontWeight: 700 }}>{formatFull(totals.covered)}</Typography>
          ) : null}
          <Typography sx={{ ...numberCellSx, fontWeight: 700 }}>{dashIfZero(totals.potentialFarmers)}</Typography>
          <Typography sx={{ ...numberCellSx, fontWeight: 700 }}>{dashIfZero(totals.potentialAcres)}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Crops grown across the covered blocks, ranked by how many blocks report each one — which is what
 * makes the list rankable at all. An alphabetical list of crop names says nothing about where the
 * volume sits.
 */
export function CropsGrown({ crops, source, blockLabel }) {
  const rows = crops || [];
  if (!rows.length) {
    return (
      <EmptyPanel
        title="Crops grown"
        message="No crops have been recorded against a block in this country yet."
      />
    );
  }

  const max = rows.reduce((acc, row) => Math.max(acc, row.blocks || 0), 0) || 1;
  const lowerBlock = (blockLabel || 'block').toLowerCase();

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={cardTitleSx}>Crops grown</Typography>
        <Typography sx={{ ...cardTitleSx, color: vizInk.secondary }}>
          {rows.length} crop{rows.length === 1 ? '' : 's'} · {sourceLabels[source] || source}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {rows.map((row) => (
          <Box key={row.cropId} sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '4px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontSize: 13, lineHeight: '18px', color: vizInk.primary, fontWeight: 500 }}>
                {row.cropName}
                {row.category ? (
                  <Box component="span" sx={{ color: vizInk.muted, fontWeight: 400 }}>
                    {' '}
                    · {titleCase(row.category)}
                  </Box>
                ) : null}
              </Typography>
              {/* The value rides the row rather than sitting on a gridline, and it keeps the count
                  readable without relying on the mark's length. */}
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: '18px',
                  color: vizInk.secondary,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatFull(row.blocks)} {lowerBlock}
                {row.blocks === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'relative',
                height: 10,
                backgroundColor: vizInk.plane,
                borderRadius: '5px',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${Math.max(1, ((row.blocks || 0) / max) * 100)}%`,
                  backgroundColor: seriesPrimary,
                  borderRadius: `0 ${markSpecs.barEndRadius}px ${markSpecs.barEndRadius}px 0`,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function EmptyPanel({ title, message }) {
  return (
    <Box sx={cardSx}>
      <Typography sx={cardTitleSx}>{title}</Typography>
      <Typography sx={{ fontSize: 13, lineHeight: '18px', color: vizInk.secondary }}>{message}</Typography>
    </Box>
  );
}

/** Same reasoning as StatTile's em dash: an uncaptured cell must not read as a measured zero. */
function dashIfZero(value) {
  const n = Number(value) || 0;
  return n > 0 ? formatFull(Math.round(n)) : '—';
}

function titleCase(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const rowGridSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(110px, 1.5fr) minmax(60px, 1.6fr) 74px 70px 62px 84px 76px',
  alignItems: 'center',
  gap: '10px',
};

/** Same grid minus the coverage column, for the bottom tier where there is nothing left to count. */
const rowGridNoCoverageSx = {
  ...rowGridSx,
  gridTemplateColumns: 'minmax(110px, 1.5fr) minmax(60px, 1.8fr) 74px 70px 84px 76px',
};

/**
 * A row that opens the next level. Pulled out to the row's own edges with a negative margin so the
 * hover surface has breathing room without knocking the columns out of line with the header.
 */
const openableRowSx = {
  cursor: 'pointer',
  padding: '3px 6px',
  margin: '-3px -6px',
  borderRadius: '6px',
  '&:hover': { backgroundColor: vizInk.plane },
  '&:focus-visible': { outline: `2px solid ${seriesPrimary}`, outlineOffset: '1px' },
};

const columnHeadSx = {
  fontSize: 10,
  lineHeight: '14px',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: vizInk.muted,
};

const numberCellSx = {
  fontSize: 13,
  lineHeight: '18px',
  textAlign: 'right',
  color: vizInk.secondary,
  fontVariantNumeric: 'tabular-nums',
};
